import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider } from "rsuite";

import constants from "utils/constants";
import { formatDateWithTime } from "utils/date-helper";

import { IAppRegistrationSecretExpiration } from "store/environments/environment-types";

import { getErrorMessage } from "api/defaults";
import { loadAppRegistrationsInfo } from "api/environments/dooap-app-registration-api";

import Modal from "components/Common/Modal/Modal";

import "./AppRegistrationInformation.less";

interface ComponentProps {
    envMoniker: string;
    isOpened: boolean;
    setAppRegistrationInfoOpened: (isOpened: boolean) => void;
}

const AppRegistrationInformation: React.FC<ComponentProps> = ({
    envMoniker,
    isOpened,
    setAppRegistrationInfoOpened,
}: ComponentProps) => {
    const { t } = useTranslation();
    const [appRegistrationInfo, setAppRegistrationInfo] = useState<
        IAppRegistrationSecretExpiration[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        if (isOpened) {
            fetchData();
        }
    }, [isOpened]);

    const fetchData = () => {
        setLoading(true);
        setErrorMessage("");
        loadAppRegistrationsInfo(envMoniker).then(([payload, status]) => {
            setLoading(false);
            if (payload && status === constants.statusCode.OK) {
                setAppRegistrationInfo(payload);
            } else {
                setErrorMessage(getErrorMessage(String(payload), status));
            }
        });
    };

    const header = <h1>{t("appRegistrationInfo.modalTitle")}</h1>;
    const footer = (
        <>
            <Button
                className={"app_registration-information__button"}
                type="submit"
                appearance="ghost"
                onClick={() => {
                    setAppRegistrationInfoOpened(false);
                }}
            >
                {t("appRegistrationInfo.closeBtnTitle")}
            </Button>
        </>
    );

    const appRegistrations = appRegistrationInfo.map(
        (ar, index) =>
            ar && (
                <div
                    key={String(index)}
                    className="app_registration-information__content"
                >
                    {index !== 0 && (
                        <Divider
                            className={
                                "app_registration-information__content__divider"
                            }
                        />
                    )}
                    <div>
                        <span>{t("appRegistrationInfo.idLabel")}</span>
                        <span>{ar.id}</span>
                    </div>
                    <div>
                        <span>{t("appRegistrationInfo.nameLabel")}</span>
                        <span>{ar.name}</span>
                    </div>
                    <div>
                        <span>{t("appRegistrationInfo.linkLabel")}</span>
                        <a
                            href={ar.azureLink}
                            target={"_blank"}
                            rel="noopener noreferrer"
                        >
                            {t("appRegistrationInfo.linkTitle")}
                        </a>
                    </div>
                    <div>
                        <span>
                            {t("appRegistrationInfo.expirationDateLabel")}
                        </span>
                        <span>
                            {formatDateWithTime(new Date(ar.expirationDate))}
                        </span>
                    </div>
                </div>
            )
    );

    return (
        <Modal
            isModalOpen={isOpened}
            onClose={() => setAppRegistrationInfoOpened(false)}
            className={"app_registration-information__modal"}
            header={header}
            footer={footer}
            isModalLoaderVisible={loading}
            closeButton={true}
            errorMessage={errorMessage}
        >
            {appRegistrations}
        </Modal>
    );
};

export default AppRegistrationInformation;
